/* eslint-disable camelcase */

import React from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'

// Local import
import TextField from './BasicFields/TextField'
import SelectUserGroupField from './BasicFields/SelectUserGroupField'
import TextAreaField from './BasicFields/TextAreaField'
import SectionField from './SectionField'
import EnsembleCard from './EnsembleCard'
import Modal from '../shared/Modal'
import ConfirmModal, { addConfirmDeleteListener } from '../shared/ConfirmModal'
import ConditionsDrawer from './ConditionsDrawer/ConditionsDrawer'
import {
  normalizeRules, serializeRules, pruneRulesForQuestions, mergeRuleErrors, flatMessages,
  groupBySections, activeRules, ruleNumberIndexFromGroups
} from './ConditionsDrawer/helpers'
import SaveFooter from '../shared/SaveFooter'
import Button from '../ui/Button'
import PlusIcon from '../ui/icons/PlusIcon'
import UploadIcon from '../ui/icons/UploadIcon'
import Switch from '../ui/Switch'
import { idGenerator, isNewRecord } from '../../utils/idHelpers'

const ANIMATION_DURATION_MS = 350
const SCROLL_DELAY_MS = 100
const DEFAULT_COEFFICIENT = 100
const DIRTY_CHECK_DELAY_MS = 500

const goToCollectsList = () => { window.location = '/collects' }

const UNSAVED_ERROR_INTRO = 'Vos modifications n\'ont pas été enregistrées.'

const FORM_ERROR_KEY = /^(?:name|sections\[[^\]]+\])$/u

const unhandledErrorMessage = errors => {
  const messages = Object.entries(errors || {})
    .filter(([key]) => !FORM_ERROR_KEY.test(key))
    .flatMap(([, value]) => flatMessages(value))
  if (messages.length === 0)
    return null

  return [UNSAVED_ERROR_INTRO, ...new Set(messages)].join(' ')
}

const scrollToFirstError = () => {
  setTimeout(() => {
    const firstInvalidField = document.querySelector('.is-invalid, [data-condition-error]')
    if (!firstInvalidField) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      return
    }

    const section = firstInvalidField.closest('.inner-nested-section')
    if (section && !section.classList.contains('show')) {
      section.classList.add('show')
      window.$(section).slideDown(ANIMATION_DURATION_MS, () => {
        firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' })
      })
    } else
      firstInvalidField.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, SCROLL_DELAY_MS)
}

const attachmentSignature = attachment => {
  if (!attachment)
    return null
  if (attachment.id)
    return `id:${attachment.id}`

  return `file:${attachment.filename || attachment.name || 'new'}`
}

const canonicalOptions = (options, dropEmpty) =>
  (options || [])
    .filter(option => !dropEmpty || String(option.content ?? '').trim() !== '')
    .map(option => ({
      id: String(option.id),
      content: option.content ?? '',
      default: Boolean(option.default)
    }))

const canonicalQuestion = question => ({
  id: String(question.id),
  context: question.context,
  position: Number(question.position),
  title: question.title || '',
  description: question.description || null,
  mandatory: Boolean(question.mandatory),
  allow_option_creation: Boolean(question.allow_option_creation),
  options_order: question.options_order || 'position',
  not_applicable: Boolean(question.not_applicable),
  spherical_photo: Boolean(question.spherical_photo),
  variable_id: question.variable_id ? String(question.variable_id) : null,
  datetime_format: question.datetime_format || null,
  coefficient: question.context === 'grade' ? Number(question.coefficient ?? DEFAULT_COEFFICIENT) : null,
  picture: attachmentSignature(question.picture),
  pdf: attachmentSignature(question.pdf),
  options: canonicalOptions(question.options, question.context === 'grade')
})

const canonicalSection = section => ({
  id: String(section.id),
  name: section.name || '',
  position: Number(section.position),
  coefficient: Number(section.coefficient),
  has_disabled_status: Boolean(section.has_disabled_status),
  removed_question_ids: (section.removed_question_ids || []).map(String).sort(),
  questions: (section.questions || []).map(canonicalQuestion)
})

const renumberIfBlankPosition = items => {
  if (items.every(item => Number.isInteger(item.position)))
    return
  items.forEach((item, index) => {
    item.position = index
  })
}

const formSnapshot = state => JSON.stringify({
  name: state.name || '',
  help_text: state.help_text || '',
  user_ids: (state.user_ids || []).map(String).sort(),
  has_prepopulate_answers: Boolean(state.has_prepopulate_answers),
  has_completion_for_additionnal: Boolean(state.has_completion_for_additionnal),
  removed_section_ids: (state.removed_section_ids || []).map(String).sort(),
  sections: (state.sections || []).map(canonicalSection),
  rules: serializeRules(state.rules || [])
})

class CollectForm extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      id: props.data.id,
      name: props.data.name,
      ensemble_id: props.data.ensemble_id,
      user_ids: props.data.user_ids,
      help_text: props.data.help_text || '',
      has_additional_answer: props.data.has_additional_answer || false,
      has_completion_for_additionnal: props.data.has_completion_for_additionnal,
      has_prepopulate_answers: props.data.has_prepopulate_answers,
      ensembles: props.data.ensembles,
      operators: props.data.operators,
      sections: props.data.sections,
      rules: props.hasConditionals ? normalizeRules(props.data.rules) : [],
      hasQuestions: props.data.has_questions,
      hasAnswers: props.data.has_answers,
      removed_section_ids: [],
      frequency: props.data.frequency,
      submitButtonText: 'Sauvegarder les modifications',
      submitButtonDisabled: false,
      errors: {},
      errorMessage: null,
      isConfirmOpen: false,
      isGradingConfirmOpen: false,
      changedCoefficients: [],
      pendingDelete: null,
      dirty: false
    }
    this.initialSnapshot = formSnapshot(this.state)
    this.initialMandatoryById = new Map()
    this.initialCoefficientsById = new Map()
    const sections = props.data.sections || []
    sections.forEach(section => {
      this.initialCoefficientsById.set(`section-${section.id}`, Number(section.coefficient))
      const questions = section.questions || []
      questions.forEach(question => {
        this.initialMandatoryById.set(String(question.id), question.mandatory)
        this.initialCoefficientsById.set(`question-${question.id}`, Number(question.coefficient))
      })
    })
  }

  componentDidMount() {
    this.removeConfirmDeleteListener = addConfirmDeleteListener(this.handleConfirmDeleteEvent)
  }

  componentDidUpdate(prevProps) {
    const { importedSections, onSectionsImported } = this.props
    if (importedSections && importedSections !== prevProps.importedSections) {
      this.setState(prevState => {
        const startPosition = prevState.sections.length
        const sectionsWithPosition = importedSections.map((section, index) => ({
          ...section,
          position: startPosition + index
        }))
        return { sections: prevState.sections.concat(sectionsWithPosition) }
      }, () => {
        if (onSectionsImported)
          onSectionsImported()
      })
    }
    this.handleNestedChange()
  }

  componentWillUnmount() {
    this.removeConfirmDeleteListener()
    clearTimeout(this.dirtyCheckTimer)
  }

  handleNestedChange = () => {
    clearTimeout(this.dirtyCheckTimer)
    this.dirtyCheckTimer = setTimeout(this.recomputeDirty, DIRTY_CHECK_DELAY_MS)
  }

  recomputeDirty = () => {
    const dirty = formSnapshot(this.state) !== this.initialSnapshot
    this.setState(prevState => prevState.dirty === dirty ? null : { dirty })
  }

  handleConfirmDeleteEvent = event => {
    this.setState({ pendingDelete: event.detail })
  }

  handleConfirmPendingDelete = () => {
    const { pendingDelete } = this.state
    this.setState({ pendingDelete: null })
    if (pendingDelete && pendingDelete.onConfirm)
      pendingDelete.onConfirm()
  }

  handleCancelPendingDelete = () => {
    this.setState({ pendingDelete: null })
  }

  handleCloseError = () => this.setState({ errorMessage: null })

  getRuleData = () => {
    if (this.cachedRulesForData !== this.state.rules) {
      this.cachedRulesForData = this.state.rules
      const groups = groupBySections(this.state.sections, activeRules(this.state.rules))
      this.cachedRuleData = {
        numberIndex: ruleNumberIndexFromGroups(groups),
        conditionCounts: groups.reduce((counts, group) => {
          counts[String(group.sectionId)] = group.entries.length
          return counts
        }, {})
      }
    }
    return this.cachedRuleData
  }

  getRuleNumberIndex = () => this.getRuleData().numberIndex

  getSectionConditionCounts = () => this.getRuleData().conditionCounts

  // Function that create FormData from nested object
  createFormData = (data, formData = new FormData(), parentKey = '') => {
    Object.keys(data).forEach(key => {
      let value = data[key]
      if (value === null || typeof value === 'undefined')
        value = ''

      const newKey = parentKey ? `${parentKey}[${key}]` : key

      if (Array.isArray(value) && value.length > 0) {
        if (typeof value[0] === 'object')
          value.forEach((item, index) => this.createFormData(item, formData, `${newKey}[${index}]`))
        else
          value.forEach(item => formData.append(`${newKey}[]`, item))
      } else
        formData.append(newKey, value)
    })
    return formData
  }


  findQuestion = (sectionId, questionId) => {
    const section = this.state.sections.find(stateSection => stateSection.id === sectionId)

    return section.questions.find(question => question.id === questionId)
  }

  formatPictureQuestion = (question, sectionId) => {
    if (question.context !== 'picture') {
      delete question.picture
      return
    }

    const pictureQuestion = this.findQuestion(sectionId, question.id)
    delete question.picture
    delete question.variable_id
    if (pictureQuestion.changed) {
      if (pictureQuestion.isDynamic) {
        question.picture = ''
        question.variable_id = pictureQuestion.variable_id || ''
      } else {
        question.picture = pictureQuestion.picture || ''
        question.variable_id = ''
      }
    }
  }

  formatPdfQuestion = (question, sectionId) => {
    if (question.context !== 'pdf') {
      delete question.pdf
      return
    }

    const pdfQuestion = this.findQuestion(sectionId, question.id)
    delete question.pdf
    delete question.variable_id
    if (pdfQuestion.changed) {
      if (pdfQuestion.isDynamic) {
        question.pdf = ''
        question.variable_id = pdfQuestion.variable_id || ''
      } else {
        // For static PDF, only set if it's a File object
        question.pdf = pdfQuestion.pdf || ''
        question.variable_id = ''
      }
    }
  }

  formatForm = () => {
    const formatedForm = JSON.parse(JSON.stringify(this.state))
    if (formatedForm.user_ids.length === 0)
      formatedForm.user_ids = ['']

    renumberIfBlankPosition(formatedForm.sections)
    formatedForm.sections.forEach(section => {
      renumberIfBlankPosition(section.questions)
      section.questions.forEach(question => {
        if (
          (question.options || []).length > 0 ||
          (question.removed_option_ids || []).length > 0
        ) {
          const removed_options = (question.removed_option_ids || []).map(id => ({ id, _destroy: true }))

          question.options.forEach(option => {
            delete option.hidden
            if (isNewRecord(option.id))
              option.client_id = option.id
          })

          question.options_attributes = question.options.concat(removed_options)
        }
        // Note: Ne pas envoyer options_attributes pour les questions sans options

        if (isNewRecord(question.id))
          question.client_id = question.id

        this.formatPictureQuestion(question, section.id)
        this.formatPdfQuestion(question, section.id)


        if (question.context !== 'description')
          delete question.description

        delete question.options
        delete question.removed_option_ids
        delete question.errors
        delete question.changed
        delete question.isDynamic
      })
      const removed_questions = (section.removed_question_ids || []).map(id => ({ id, _destroy: true }))
      const questions = section.questions.concat(removed_questions)

      if (questions.length > 0)
        section.questions_attributes = questions
      else
        section['questions_attributes[]'] = ''

      delete section.questions
      delete section.removed_question_ids
      delete section.errors
    })

    const removed_sections = (formatedForm.removed_section_ids || []).map(id => ({ id, _destroy: true }))
    const sections = formatedForm.sections.concat(removed_sections)

    if (sections.length > 0)
      formatedForm.sections_attributes = sections
    else
      formatedForm['sections_attributes[]'] = ''

    delete formatedForm.sections
    delete formatedForm.removed_section_ids
    delete formatedForm.errors
    delete formatedForm.ensemble_id
    delete formatedForm.id
    delete formatedForm.operators
    delete formatedForm.ensembles
    delete formatedForm.submitButtonText
    delete formatedForm.submitButtonDisabled
    delete formatedForm.frequency
    delete formatedForm.has_additional_answer
    delete formatedForm.hasQuestions
    delete formatedForm.hasAnswers
    delete formatedForm.isConfirmOpen
    delete formatedForm.isGradingConfirmOpen
    delete formatedForm.changedCoefficients
    delete formatedForm.errorMessage
    delete formatedForm.pendingDelete
    delete formatedForm.rules

    const rulesAttributes = serializeRules(this.state.rules)
    if (rulesAttributes.length > 0)
      formatedForm.rules_attributes = rulesAttributes

    return formatedForm
  }

  performSubmit = () => {
    const formatedForm = this.formatForm()

    // To post pictures we need to create a FormData object
    const result = this.createFormData(formatedForm, new FormData(), 'collect')

    this.setState({
      submitButtonText: 'Patientez...', submitButtonDisabled: true, errors: {}, errorMessage: null
    })
    const token = document.querySelector('meta[name=\'csrf-token\']').getAttribute('content')

    fetch(`/collects/${this.state.id}`, {
      method: 'PATCH',
      headers: {
        'X-CSRF-Token': token
      },
      body: result
    })
      .then(response => {
        if (response.ok)
          return response.json()

        if (response.status === 401 || response.status === 403)
          throw new Error('Session expirée. Veuillez vous reconnecter.')
        if (response.status >= 500)
          throw new Error('Erreur serveur. Veuillez réessayer.')
        if (response.status === 400)
          throw new Error('Requête invalide. Le questionnaire est peut-être trop volumineux.')

        throw new Error('Une erreur est survenue. Veuillez réessayer.')
      })
      .then(data => {
        if (data.success)
          goToCollectsList()
        else {
          this.setState(prevState => {
            const errors = mergeRuleErrors(data.formatted_errors, {
              rules: prevState.rules,
              sections: prevState.sections,
              numberIndex: this.getRuleNumberIndex()
            })
            return {
              errors,
              errorMessage: unhandledErrorMessage(errors),
              submitButtonText: 'Sauvegarder les modifications',
              submitButtonDisabled: false
            }
          }, scrollToFirstError)
        }
      })
      .catch(error => {
        this.setState({
          errorMessage: error.message || 'Erreur de connexion. Vérifiez votre réseau.',
          submitButtonText: 'Sauvegarder les modifications',
          submitButtonDisabled: false
        }, () => window.scrollTo({ top: 0, behavior: 'smooth' }))
      })
  }

  hasMandatoryTurnedOn = () => {
    for (const section of this.state.sections) {
      const questions = section.questions || []
      for (const question of questions) {
        const id = String(question.id)
        const current = question.mandatory

        if (id.startsWith('new-') && current === true)
          return true

        const original = this.initialMandatoryById.get(id)

        if (original === false && current === true)
          return true
      }
    }

    return false
  }

  computeChangedCoefficients = () => {
    const changed = []

    for (const section of this.state.sections) {
      const sectionKey = `section-${section.id}`
      const questions = section.questions || []
      const gradeQuestions = questions.filter(question => question.context === 'grade')
      const isGradable = gradeQuestions.length > 0

      if (isGradable && this.initialCoefficientsById.has(sectionKey) &&
          Number(section.coefficient) !== this.initialCoefficientsById.get(sectionKey))
        changed.push({ key: sectionKey, label: `Section « ${section.name} »` })

      for (const question of gradeQuestions) {
        const questionKey = `question-${question.id}`
        if (this.initialCoefficientsById.has(questionKey) &&
            Number(question.coefficient) !== this.initialCoefficientsById.get(questionKey)) {
          changed.push({
            key: questionKey,
            label: `Question « ${question.title} » (section « ${section.name} »)`
          })
        }
      }
    }

    return changed
  }

  maybeOpenGradingModal = () => {
    if (!this.state.hasAnswers)
      return false

    const changedCoefficients = this.computeChangedCoefficients()
    if (changedCoefficients.length === 0)
      return false

    this.setState({ isGradingConfirmOpen: true, changedCoefficients })
    return true
  }

  handleSubmit = event => {
    event.preventDefault()

    if (this.state.hasQuestions && this.hasMandatoryTurnedOn()) {
      this.setState({ isConfirmOpen: true })
      return
    }
    if (this.maybeOpenGradingModal())
      return

    this.performSubmit()
  }

  handleCloseConfirmModal = () => {
    this.setState({ isConfirmOpen: false })
  }

  handleConfirmAndSubmit = () => {
    this.setState({ isConfirmOpen: false }, () => {
      if (this.maybeOpenGradingModal())
        return

      this.performSubmit()
    })
  }

  handleCloseGradingConfirmModal = () => {
    this.setState({ isGradingConfirmOpen: false })
  }

  handleConfirmGradingAndSubmit = () => {
    this.setState({ isGradingConfirmOpen: false }, () => this.performSubmit())
  }

  handleChange = event => this.setState({ [event.target.name]: event.target.value })

  handleOperatorsChange = event => {
    const user_ids = Array.from(event.target.selectedOptions, option => option.value)
    this.setState({ user_ids })
  }

  handleHasPrepopulateAnswersChange = event => {
    this.setState({ has_prepopulate_answers: event.target.checked })
  }

  handleCompletionAdditionnalChange = event => {
    this.setState({ has_completion_for_additionnal: event.target.checked })
  }

  getErrors = index => () => this.state.errors[`sections[${index}]`]

  handleOnDragEnd = result => {
    const { destination, source } = result

    if (!destination)
      return

    if (destination.droppableId === source.droppableId && destination.index === source.index)
      return

    this.setState(prevState => {
      const movedSection = prevState.sections[source.index]
      const newSections = prevState.sections

      newSections.splice(source.index, 1)
      newSections.splice(destination.index, 0, movedSection)
      newSections.forEach((section, index) => {
        section.position = index
      })

      return { sections: newSections }
    })
  }

  handleClickNewSection = () => {
    const newSection = {
      id: idGenerator(),
      name: 'Section',
      position: this.state.sections.length,
      coefficient: DEFAULT_COEFFICIENT,
      questions: []
    }

    this.setState(prevState => ({ sections: prevState.sections.concat(newSection) }))
  }


  handleSectionChange = index => section => {
    this.setState(prevState => {
      const { sections } = prevState

      sections[index] = section

      return { sections }
    })
  }

  handleRulesChange = rules => this.setState({ rules })

  handleQuestionsRemoved = questionIds =>
    this.setState(prevState => ({ rules: pruneRulesForQuestions(prevState.rules, questionIds) }))

  onDuplicateSection = duplicateData => async event => {
    event.stopPropagation()
    const data = await duplicateData()
    this.setState(prevState => {
      data.position = prevState.sections.length
      return { sections: [...prevState.sections, data] }
    })
  }

  handleRemoveSection = sectionId => event => {
    event.stopPropagation()
    this.setState(prevState => {
      let newlyRemovedSections = []
      const removedSection = prevState.sections.find(section => section.id === sectionId)
      const removedQuestionIds = (removedSection?.questions || []).map(question => question.id)
      const sections = prevState.sections.filter(section => section.id !== sectionId)

      if (!isNewRecord(sectionId))
        newlyRemovedSections = prevState.removed_section_ids.concat(sectionId)

      sections.forEach((section, index) => {
        section.position = index
      })

      return {
        sections,
        removed_section_ids: newlyRemovedSections,
        rules: pruneRulesForQuestions(prevState.rules, removedQuestionIds)
      }
    })
  }

  draggableSections = () => {
    const conditionCounts = this.getSectionConditionCounts()

    return this.state.sections.map((section, index) => (
      <Draggable
        draggableId={`draggable-section-${index}`}
        key={`draggable-section-${index}`}
        index={index}
      >
        {provided => (
          <div ref={provided.innerRef} {...provided.draggableProps}>
            <SectionField
              key={`sections.${section.id}`}
              fieldName={`sections.${section.id}`}
              collectFrequency={this.state.frequency}
              section={section}
              data={this.props.data}
              hasConditionals={this.props.hasConditionals}
              hasPlan={this.props.hasPlan}
              hasDynamicOptions={this.props.hasDynamicOptions}
              conditionsCount={conditionCounts[String(section.id)] || 0}
              rules={this.state.rules}
              ruleNumberIndex={this.getRuleNumberIndex()}
              handleSectionChange={this.handleSectionChange(index)}
              handleDuplicateSection={this.onDuplicateSection}
              handleRemoveSection={this.handleRemoveSection(section.id)}
              getErrors={this.getErrors(section.id)}
              dragHandleProps={provided.dragHandleProps}
              onOpenConditions={this.props.onOpenConditions}
              onAddQuestionCondition={this.props.onAddQuestionCondition}
              onQuestionsRemoved={this.handleQuestionsRemoved}
              onNestedChange={this.handleNestedChange}
            />
          </div>
        )}
      </Draggable>
    ))
  }


  render() {
    const ensemble = this.state.ensembles.find(ens => ens.id === this.state.ensemble_id)

    return (
      <>
        <form onSubmit={this.handleSubmit}>
          {this.state.errorMessage ? (
            <div className="alert alert-dismissible alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle mr-2" />
              {this.state.errorMessage}
              <button type="button" className="close" data-dismiss="alert" onClick={this.handleCloseError}>
                <span>&times;</span>
              </button>
            </div>
          ) : null}
          <div className="row">
            <div className="col-md-6">
              {ensemble ? <EnsembleCard ensemble={ensemble} /> : null}
              <div className="mt-3">
                <TextField
                  name="name"
                  label="Nom"
                  value={this.state.name}
                  errors={this.state.errors.name}
                  onChange={this.handleChange}
                />
              </div>
              <SelectUserGroupField
                name="user_ids"
                className="selectpicker"
                label="Opérateurs"
                data={this.state.operators}
                value={this.state.user_ids}
                multiple
                countLabel
                selectedFirst
                onChange={this.handleOperatorsChange}
              />
              {this.state.frequency === 'recurrent' ? (
                <Switch
                  id="has_prepopulate_answers"
                  checked={this.state.has_prepopulate_answers}
                  label="Pré-remplir le contrôle en cours avec les réponses du contrôle précédent"
                  onChange={this.handleHasPrepopulateAnswersChange}
                />
              ) : null}
              {this.state.has_additional_answer ? (
                <Switch
                  id="has_completion_for_additionnal"
                  checked={this.state.has_completion_for_additionnal}
                  className="tw:mt-4"
                  label="Proposer le contrôle supplémentaire après premier contrôle"
                  onChange={this.handleCompletionAdditionnalChange}
                />
              ) : null}
            </div>
            <div className="col">
              <TextAreaField
                name="help_text"
                label="Texte d'aide"
                rows="9"
                value={this.state.help_text}
                onChange={this.handleChange}
              />
            </div>
          </div>
          <DragDropContext onDragEnd={this.handleOnDragEnd}>
            <Droppable droppableId="droppable-sections">
              {provided => (
                <div ref={provided.innerRef} {...provided.droppableProps} className="tw:mt-4">
                  {this.draggableSections()}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <div className="tw:flex tw:gap-3 tw:mt-3">
            <Button
              variant="neutral"
              iconNode={<PlusIcon className="tw:w-4 tw:h-4" />}
              onClick={this.handleClickNewSection}
            >
              Ajouter une section
            </Button>
            <Button
              variant="neutral"
              iconNode={<UploadIcon className="tw:w-4 tw:h-4" />}
              onClick={this.props.onOpenImportModal}
            >
              Importer un questionnaire
            </Button>
          </div>
        </form>
        <SaveFooter
          submitText={this.state.submitButtonText}
          disabled={this.state.submitButtonDisabled}
          dirty={this.state.dirty}
          onSave={this.handleSubmit}
          onCancel={goToCollectsList}
        />
        <Modal
          isOpen={this.state.isConfirmOpen}
          title={(
            <>
              <i className="fas fa-plus font-weight-normal" />
              {' '}
              Ajouter une question obligatoire
            </>
          )}
          footer={
            <div className="d-flex justify-content-end w-100">
              <button
                type="button"
                className="btn btn-outline-light text-dark border border-muted mr-2"
                onClick={this.handleCloseConfirmModal}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={this.handleConfirmAndSubmit}
              >
                Ajouter quand même
              </button>
            </div>
          }
          onClose={this.handleCloseConfirmModal}
        >
          <div>
            <div
              className="mandatory-warning-alert d-flex align-items-center px-3 py-2 mb-4"
              role="alert"
            >
              <i className="fas fa-exclamation-circle mr-2" />
              <span>
                Question obligatoire
              </span>
            </div>
            <p className="mb-3">
              L&apos;ajout d&apos;une question obligatoire peut impacter les opérateurs
              qui ont déjà téléchargé le questionnaire : leurs réponses seront rejetées
              lors de la synchronisation si cette question n&apos;est pas remplie.
            </p>
            <strong>Que faire ?</strong>
            <p className="mt-2 mb-0">
              Pensez à informer les opérateurs concernés de re-synchroniser leur application
              avant de continuer à saisir des réponses.
            </p>
          </div>
        </Modal>
        <Modal
          isOpen={this.state.isGradingConfirmOpen}
          title="Modification des éléments de notation"
          footer={
            <div className="d-flex justify-content-end w-100">
              <button
                type="button"
                className="btn btn-outline-light text-dark border border-muted mr-2"
                onClick={this.handleCloseGradingConfirmModal}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={this.handleConfirmGradingAndSubmit}
              >
                Sauvegarder quand même
              </button>
            </div>
          }
          onClose={this.handleCloseGradingConfirmModal}
        >
          <div>
            <div
              className="mandatory-warning-alert d-flex align-items-center px-3 py-2 mb-4"
              role="alert"
            >
              <i className="fas fa-exclamation-circle mr-2" />
              <span>
                Recalcul des notes
              </span>
            </div>
            <p className="mb-3">
              La modification d&apos;un coefficient sur une collecte qui a déjà des réponses
              déclenchera un recalcul des notes et des taux de satisfaction.
              Les moyennes affichées dans les comparatifs et exports peuvent évoluer.
            </p>
            {this.state.changedCoefficients.length > 0 &&
              <div className="mb-3">
                <strong>Coefficient(s) modifié(s) :</strong>
                <ul className="mb-0 mt-1">
                  {this.state.changedCoefficients.map(changedCoefficient =>
                    <li key={changedCoefficient.key}>{changedCoefficient.label}</li>
                  )}
                </ul>
              </div>}
            <strong>Que faire ?</strong>
            <p className="mt-2 mb-0">
              Pensez à informer les utilisateurs concernés que les notes vont être recalculées
              avant de continuer.
            </p>
          </div>
        </Modal>
        <ConfirmModal
          isOpen={this.state.pendingDelete !== null}
          title={this.state.pendingDelete ? this.state.pendingDelete.title : ''}
          confirmLabel="Supprimer"
          onConfirm={this.handleConfirmPendingDelete}
          onCancel={this.handleCancelPendingDelete}
        >
          <p className="mb-3">
            {this.state.pendingDelete ? this.state.pendingDelete.message : ''}
          </p>
          <p className="mb-0 text-muted">
            La suppression sera effective lors de la sauvegarde du formulaire.
          </p>
        </ConfirmModal>
        <ConditionsDrawer
          isOpen={Boolean(this.props.conditionsOpen)}
          initialSectionFilterId={this.props.conditionsFilterId}
          initialQuestionId={this.props.conditionsQuestionId}
          rules={this.state.rules}
          sections={this.state.sections}
          variables={this.props.data.variables}
          gradeConfig={{
            gradeMin: this.props.data.grade_zero_start ? 0 : 1,
            gradeSize: this.props.data.grade_size
          }}
          onChange={this.handleRulesChange}
          onClose={this.props.onCloseConditions}
        />
      </>
    )
  }
}

export default CollectForm
